import { useCamaraFotos } from '@/components/fotos/CamaraFotos';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAvanceSprint, useCompletarArrastradas, useRegistrarAvance } from '@/hooks/useAvance';
import { useCausas } from '@/hooks/useCausas';
import { useObra } from '@/hooks/useObras';
import { esSoloLectura } from '@/lib/cliente';
import { cn } from '@/lib/utils';
import type {
  AvanceSubPartida,
  ControlSubPartida,
  EstadoAvance,
  RegistrarAvance,
} from '@adelante/shared';
import {
  Camera,
  CaretDown,
  CaretLeft,
  CaretRight,
  CheckCircle,
  Circle,
  CircleHalf,
  CircleNotch,
  Images,
  MagnifyingGlass,
  Warning,
  WarningCircle,
  X,
  XCircle,
} from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ControlCalidadFila,
  bloqueoCalidad,
  permiteAvance,
  useControlesCalidad,
} from './ControlCalidadFila';
import { RegistrarNCDialog } from './RegistrarNCDialog';

/** % preestablecidos para captura rápida inline (réplica del panel original). */
const PRESETS = [0, 25, 50, 75, 100] as const;

/** Deriva el estado visual de una sub-partida a partir de su avance. */
function estadoDe(sp: AvanceSubPartida): EstadoAvance {
  if (sp.nc_causa) return 'nc';
  if (sp.completada) return 'completada';
  if (sp.pct_completado > 0) return 'en_progreso';
  return 'sin_iniciar';
}

const ESTILO: Record<
  EstadoAvance,
  { stripe: string; chip: string; label: string; Icon: typeof Circle }
> = {
  sin_iniciar: {
    stripe: 'bg-zinc-300',
    chip: 'bg-zinc-100 text-zinc-600',
    label: 'Sin iniciar',
    Icon: Circle,
  },
  en_progreso: {
    stripe: 'bg-blue-500',
    chip: 'bg-blue-50 text-blue-700',
    label: 'En progreso',
    Icon: CircleHalf,
  },
  completada: {
    stripe: 'bg-adelante-primary',
    chip: 'bg-adelante-primary/20 text-adelante-primary-dark',
    label: 'Completada',
    Icon: CheckCircle,
  },
  nc: {
    stripe: 'bg-red-500',
    chip: 'bg-red-50 text-red-700',
    label: 'No cumplió',
    Icon: XCircle,
  },
};

export function CampoAvancePantalla() {
  const { codigo } = useParams<{ codigo: string }>();
  const avanceQ = useAvanceSprint(codigo);
  const obraQ = useObra(codigo);
  const causasQ = useCausas();
  // Calidad: controles por sub-partida (la inspección bloquea el inicio).
  const calidad = useControlesCalidad(codigo);
  const abrirCamara = useCamaraFotos();
  const controles = calidad.controles;

  const mutation = useRegistrarAvance(codigo ?? '');
  const completarArrastradas = useCompletarArrastradas(codigo ?? '');
  const soloLectura = esSoloLectura();

  const [ncDe, setNcDe] = useState<AvanceSubPartida | null>(null);
  const [busqueda, setBusqueda] = useState('');
  // Partidas colapsadas (vista árbol). null = usar el default (colapsar las que
  // no tienen trabajo del sprint actual). Una vez que el usuario togglea, manda
  // su selección manual.
  const [colapsadasManual, setColapsadasManual] = useState<Set<string> | null>(null);
  // Foco: si venimos desde una celda de la matriz (?partida=1.3) mostramos solo
  // esa partida.
  const [params, setParams] = useSearchParams();
  const partidaFoco = params.get('partida');

  // Mapa codigo→descripcion para mostrar la causa NC legible en la fila.
  const causaLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of causasQ.data?.data ?? []) m.set(c.codigo, c.descripcion);
    return m;
  }, [causasQ.data]);

  const avance = avanceQ.data; // AvanceSprint | null | undefined

  // Sub-partidas agrupadas por partida.
  const porPartida = useMemo(() => {
    const map = new Map<string, { nombre: string; subs: AvanceSubPartida[] }>();
    for (const sp of avance?.sub_partidas ?? []) {
      const k = sp.partida_codigo;
      const g = map.get(k) ?? { nombre: sp.partida_nombre, subs: [] };
      g.subs.push(sp);
      map.set(k, g);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [avance]);

  // Vista: aplica foco de partida (matriz) + búsqueda de texto.
  const porPartidaVista = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return porPartida
      .filter(([cod]) => !partidaFoco || cod === partidaFoco)
      .map(([cod, g]) => {
        const subs = q
          ? g.subs.filter((s) => `${s.codigo} ${s.nombre}`.toLowerCase().includes(q))
          : g.subs;
        return [cod, { ...g, subs }] as const;
      })
      .filter(([, g]) => g.subs.length > 0);
  }, [porPartida, partidaFoco, busqueda]);

  // Default: solo se expanden las partidas con trabajo del SPRINT ACTUAL. Las
  // demás (atrasadas, futuras, ya cerradas) quedan colapsadas para no penalizar
  // el render en obras de sprint alto; el header marca cuántas atrasadas tienen.
  const sprintActual = avance?.sprint ?? 0;
  const defaultColapsadas = useMemo(() => {
    const s = new Set<string>();
    for (const [cod, g] of porPartida) {
      const tieneActual = g.subs.some((sp) => sp.sprint_numero === sprintActual);
      if (!tieneActual) s.add(cod);
    }
    return s;
  }, [porPartida, sprintActual]);
  const colapsadas = colapsadasManual ?? defaultColapsadas;

  // Con búsqueda o foco, todo abierto; si no, abierto salvo lo colapsado.
  const forzarAbierto = busqueda.trim() !== '' || partidaFoco !== null;
  function togglePartida(cod: string) {
    setColapsadasManual(() => {
      const next = new Set(colapsadas);
      if (next.has(cod)) next.delete(cod);
      else next.add(cod);
      return next;
    });
  }
  function limpiarFoco() {
    const next = new URLSearchParams(params);
    next.delete('partida');
    setParams(next, { replace: true });
  }

  // ---- Acción de registro compartida ----
  // `silencioso` evita el toast de éxito en los toques rápidos de % (el número
  // de la fila ya da feedback); igual avisa si quedó encolado offline o si falló.
  async function registrar(body: RegistrarAvance, etiqueta: string, silencioso = false) {
    // Calidad: se valida también en el cliente para no encolar offline un inicio bloqueado.
    const sp = avance?.sub_partidas.find((s) => s.sub_partida_id === body.sub_partida_id);
    const nuevoPct = body.completada ? 100 : (body.pct_completado ?? 0);
    if (
      sp &&
      !permiteAvance(bloqueoCalidad(controles.get(sp.sub_partida_id), sp.pct_completado), nuevoPct)
    ) {
      toast.error('Sub-partida bloqueada por calidad', {
        description: `${sp.codigo}: requiere la inspección aprobada.`,
      });
      return;
    }
    try {
      const res = await mutation.mutateAsync(body);
      if (res.encolado) {
        toast.info('Guardado sin conexión', {
          description: `${etiqueta} — se sincronizará al reconectar`,
        });
      } else if (!silencioso) {
        toast.success('Avance registrado', { description: etiqueta });
      }
    } catch (e) {
      toast.error('No se pudo registrar', {
        description: e instanceof Error ? e.message : 'Error desconocido',
      });
      calidad.refetch();
    }
  }

  /** Fija el % de una sub-partida (preset o exacto). 100 = completada. */
  function fijarPct(sp: AvanceSubPartida, pct: number) {
    registrar(
      {
        sub_partida_id: sp.sub_partida_id,
        pct_completado: pct,
        completada: pct >= 100,
        nc_causa: null,
        nc_nota: null,
      },
      `${sp.codigo} · ${pct}%`,
      true,
    );
  }

  function confirmarNC(causaCodigo: string, nota: string | null) {
    if (!ncDe) return;
    registrar(
      {
        sub_partida_id: ncDe.sub_partida_id,
        pct_completado: ncDe.pct_completado,
        completada: false,
        nc_causa: causaCodigo,
        nc_nota: nota,
      },
      `${ncDe.codigo} · NC`,
    );
    setNcDe(null);
  }

  // ---------------- Render ----------------

  // Cargando
  if (avanceQ.isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
        Cargando avance…
      </div>
    );
  }

  // Error real (no 404)
  if (avanceQ.isError) {
    return (
      <div className="space-y-4">
        <Volver />
        <Card className="flex flex-col items-center gap-2 p-6 text-center">
          <WarningCircle className="h-8 w-8 text-red-500" weight="fill" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">No se pudo cargar el avance de {codigo}.</p>
          <Button variant="outline" size="sm" onClick={() => avanceQ.refetch()}>
            Reintentar
          </Button>
        </Card>
      </div>
    );
  }

  // Obra no habilitada (sin obra_estado) → aviso; la habilita el admin.
  if (avance === null) {
    return <NoHabilitadaVista codigo={codigo ?? ''} />;
  }

  if (!avance) return null;

  // Subpartidas atrasadas (de sprints anteriores) aún pendientes → habilita el
  // atajo "Completar atrasadas".
  const atrasadasPendientes = avance.sub_partidas.filter(
    (s) => s.arrastrada && !s.completada && s.pct_completado < 100,
  ).length;

  return (
    <div className="space-y-4">
      <Volver />

      {/* Encabezado de la obra + avance del sprint */}
      <Card className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-mono text-lg font-semibold">{avance.obra_codigo}</p>
            <p className="text-xs text-muted-foreground">
              {obraQ.data?.data?.nombre_modelo ?? 'Sin modelo'}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() =>
                  abrirCamara({ obra: avance.obra_codigo, etiqueta: avance.obra_codigo })
                }
                className="rounded-md border border-border p-1.5 hover:bg-zinc-50"
                title="Tomar fotos de la obra"
                aria-label="Tomar fotos de la obra"
              >
                <Camera className="h-4 w-4" weight="bold" aria-hidden="true" />
              </button>
              <Link
                to={`/campo/fotos/${encodeURIComponent(avance.obra_codigo)}`}
                className="rounded-md border border-border p-1.5 hover:bg-zinc-50"
                title="Ver fotos de la obra"
                aria-label="Ver fotos de la obra"
              >
                <Images className="h-4 w-4" weight="bold" aria-hidden="true" />
              </Link>
            </div>
            <Badge variant="outline" className="font-mono">
              {avance.tipo_casa}
            </Badge>
            <span className="text-xs text-muted-foreground">Sprint {avance.sprint}</span>
          </div>
        </div>

        {/* Barra de avance del sprint */}
        <div>
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="font-medium text-muted-foreground">Avance del sprint</span>
            <span className="font-semibold tabular-nums">{avance.avance_sprint}%</span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-zinc-200">
            <div
              className="h-full rounded-full bg-adelante-primary transition-all"
              style={{ width: `${Math.min(100, avance.avance_sprint)}%` }}
            />
          </div>
        </div>
      </Card>

      {/* Buscador + foco de partida (al venir desde una celda de la matriz) */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <MagnifyingGlass
            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            weight="bold"
            aria-hidden="true"
          />
          <Input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar sub-partida…"
            className="pl-8"
          />
        </div>
        {partidaFoco && (
          <button
            type="button"
            onClick={limpiarFoco}
            className="inline-flex items-center gap-1 rounded-full border border-adelante-primary bg-adelante-primary/10 px-2.5 py-1 text-xs font-medium text-adelante-primary-dark"
            title="Quitar filtro de partida"
          >
            Partida {partidaFoco}
            <X className="h-3 w-3" weight="bold" aria-hidden="true" />
          </button>
        )}
      </div>

      <p className="flex items-center gap-3 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-1.5 rounded-sm bg-zinc-900" aria-hidden="true" />
          Sprint actual
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-1.5 rounded-sm bg-red-500" aria-hidden="true" />
          Atrasada
        </span>
      </p>

      {/* Atajo: completar todas las atrasadas (sprints anteriores) de la obra. */}
      {atrasadasPendientes > 0 && !soloLectura && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
          <p className="text-xs text-amber-800">
            <strong>{atrasadasPendientes}</strong> sub-partida
            {atrasadasPendientes === 1 ? '' : 's'} atrasada
            {atrasadasPendientes === 1 ? '' : 's'} de sprints anteriores.
          </p>
          <Button
            variant="outline"
            size="sm"
            disabled={completarArrastradas.isPending}
            onClick={() => {
              completarArrastradas.mutate(undefined, {
                onSuccess: (r) =>
                  toast.success(
                    `${r.error === null ? r.data.completadas : 0} atrasadas completadas`,
                  ),
                onError: (e) =>
                  toast.error('No se pudo completar', {
                    description: e instanceof Error ? e.message : 'Error',
                  }),
              });
            }}
          >
            {completarArrastradas.isPending ? (
              <>
                <CircleNotch
                  className="mr-1.5 h-3.5 w-3.5 animate-spin"
                  weight="bold"
                  aria-hidden="true"
                />
                Completando…
              </>
            ) : (
              'Completar atrasadas'
            )}
          </Button>
        </div>
      )}

      {/* Sub-partidas agrupadas por partida (árbol colapsable) */}
      <div className="space-y-3">
        {porPartidaVista.length === 0 && (
          <Card className="p-6 text-center text-sm text-muted-foreground">
            {busqueda
              ? `Ninguna sub-partida coincide con «${busqueda}».`
              : partidaFoco
                ? 'Esta partida no tiene sub-partidas pendientes en el sprint actual.'
                : 'No hay sub-partidas para este sprint.'}
          </Card>
        )}
        {porPartidaVista.map(([partidaCodigo, grupo]) => {
          const abierta = forzarAbierto || !colapsadas.has(partidaCodigo);
          const delSprint = grupo.subs.filter((s) => s.sprint_numero === sprintActual).length;
          const atrasadas = grupo.subs.filter(
            (s) => s.arrastrada && !s.completada && s.pct_completado < 100,
          ).length;
          return (
            <section
              key={partidaCodigo}
              className="overflow-hidden rounded-md border border-border"
            >
              <button
                type="button"
                onClick={() => togglePartida(partidaCodigo)}
                className="flex w-full items-center gap-2 bg-white px-3 py-2 text-left hover:bg-zinc-50"
              >
                {abierta ? (
                  <CaretDown
                    className="h-4 w-4 shrink-0 text-muted-foreground"
                    weight="bold"
                    aria-hidden="true"
                  />
                ) : (
                  <CaretRight
                    className="h-4 w-4 shrink-0 text-muted-foreground"
                    weight="bold"
                    aria-hidden="true"
                  />
                )}
                <span className="font-mono text-xs font-semibold">{partidaCodigo}</span>
                <span className="truncate text-sm font-medium">{grupo.nombre}</span>
                <span className="ml-auto flex shrink-0 items-center gap-1.5 text-[11px]">
                  {delSprint > 0 && (
                    <span className="rounded-full bg-zinc-900 px-1.5 py-0.5 font-medium text-white">
                      {delSprint} del sprint
                    </span>
                  )}
                  {atrasadas > 0 && (
                    <span className="rounded-full bg-red-100 px-1.5 py-0.5 font-medium text-red-700">
                      {atrasadas} atrasada{atrasadas === 1 ? '' : 's'}
                    </span>
                  )}
                  <span className="text-muted-foreground">{grupo.subs.length}</span>
                </span>
              </button>
              {abierta && (
                <div className="space-y-2 border-t border-border bg-zinc-50/50 p-2">
                  {grupo.subs.map((sp) => (
                    <SubPartidaFila
                      key={sp.sub_partida_id}
                      sp={sp}
                      sprintActual={sprintActual}
                      causaLabel={causaLabel}
                      soloLectura={soloLectura}
                      onSetPct={(pct) => fijarPct(sp, pct)}
                      onNC={() => setNcDe(sp)}
                      control={controles.get(sp.sub_partida_id)}
                      onAbrirControl={(tipo) => calidad.abrir(sp.sub_partida_id, tipo)}
                      onFoto={() =>
                        abrirCamara({
                          obra: avance.obra_codigo,
                          sub_partida_id: sp.sub_partida_id,
                          etiqueta: `${avance.obra_codigo} · ${sp.nombre}`,
                        })
                      }
                      abriendoControl={calidad.abriendo}
                    />
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>

      <RegistrarNCDialog
        subPartida={ncDe}
        pending={mutation.isPending}
        onClose={() => setNcDe(null)}
        onConfirmar={confirmarNC}
      />
    </div>
  );
}

// ============================================================================
// Fila de sub-partida
// ============================================================================

interface FilaProps {
  sp: AvanceSubPartida;
  /** Sprint actual de la obra (para colorear: negro=actual, rojo=atrasada). */
  sprintActual: number;
  causaLabel: Map<string, string>;
  /** Rol 'visualizador': deshabilita toda captura (presets, % exacto, NC). */
  soloLectura: boolean;
  /** Fija el % (preset o exacto). 100 marca completada. */
  onSetPct: (pct: number) => void;
  onNC: () => void;
  /** Control de calidad de la sub-partida (si tiene fichas). */
  control?: ControlSubPartida;
  onAbrirControl: (tipo: 'inspeccion' | 'evaluacion') => void;
  abriendoControl: boolean;
  /** Tomar fotos ligadas a esta sub-partida. */
  onFoto: () => void;
}

function SubPartidaFila({
  sp,
  sprintActual,
  causaLabel,
  soloLectura,
  onSetPct,
  onNC,
  control,
  onAbrirControl,
  abriendoControl,
  onFoto,
}: FilaProps) {
  const estado = estadoDe(sp);
  const bloqueo = bloqueoCalidad(control, sp.pct_completado);
  const est = ESTILO[estado];
  const actual = Math.round(sp.pct_completado);
  const piso = Math.round(sp.piso_pct);

  // Input exacto: se sincroniza cuando cambia el avance (tras optimista/refetch).
  const [exacto, setExacto] = useState(String(actual));
  useEffect(() => {
    setExacto(String(Math.round(sp.pct_completado)));
  }, [sp.pct_completado]);

  function aplicarExacto() {
    const n = Number(exacto);
    // Validar: rango 0-100 y >= piso (regla 1b: no bajar del avance cerrado).
    if (!Number.isNaN(n) && n >= piso && n <= 100 && n !== actual && permiteAvance(bloqueo, n))
      onSetPct(n);
    else setExacto(String(actual));
  }

  // Dos franjas (como la app vieja):
  //   IZQUIERDA = estado de avance (gris/azul/verde/rojo-NC).
  //   DERECHA   = relación con el sprint: negra = del sprint actual (programada),
  //               roja = atrasada (sprint anterior, aún pendiente). Nada si futura.
  const esActual = sp.sprint_numero === sprintActual;
  const esAtrasada = sp.arrastrada && estado !== 'completada';
  const rightClass = esActual ? 'bg-zinc-900' : esAtrasada ? 'bg-red-500' : 'bg-transparent';

  return (
    <Card className="overflow-hidden">
      <div className="flex">
        {/* Franja izquierda: estado de avance. */}
        <div className={cn('w-1.5 shrink-0', est.stripe)} aria-hidden="true" />
        <div className="min-w-0 flex-1 p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-sm font-medium">
                <span className="truncate">{sp.nombre}</span>
                <button
                  type="button"
                  onClick={onFoto}
                  className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-zinc-100 hover:text-foreground"
                  title="Tomar fotos de esta sub-partida"
                  aria-label={`Tomar fotos de ${sp.nombre}`}
                >
                  <Camera className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
                </button>
                {sp.es_critica && (
                  <Warning
                    className="h-3.5 w-3.5 shrink-0 text-amber-500"
                    weight="fill"
                    aria-label="Crítica"
                  />
                )}
              </p>
              <p className="flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
                <span>
                  {sp.codigo} · peso {sp.peso}%
                </span>
                {/* Marca de sprint: ● negra = sprint actual; ● roja = arrastrada */}
                <span
                  className={cn(
                    'inline-flex items-center gap-0.5',
                    sp.arrastrada ? 'font-semibold text-red-600' : 'text-foreground',
                  )}
                  title={
                    sp.arrastrada
                      ? `Arrastrada del sprint ${sp.sprint_numero} — quedó pendiente`
                      : `Programada en el sprint ${sp.sprint_numero}`
                  }
                >
                  <span aria-hidden="true">●</span> S{sp.sprint_numero}
                </span>
                {piso > 0 && (
                  <span className="text-amber-700" title="Mínimo permitido por el último cierre">
                    piso {piso}%
                  </span>
                )}
              </p>
            </div>
            <span
              className={cn(
                'flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
                est.chip,
              )}
            >
              <est.Icon className="h-3.5 w-3.5" weight="fill" aria-hidden="true" />
              {estado === 'nc' ? est.label : `${actual}%`}
            </span>
          </div>

          {/* Detalle de NC */}
          {estado === 'nc' && (
            <p className="mt-1 text-xs text-red-700">
              {causaLabel.get(sp.nc_causa ?? '') ?? sp.nc_causa}
              {sp.nc_nota ? ` — ${sp.nc_nota}` : ''}
            </p>
          )}

          {/* % preestablecidos inline + exacto + NC (captura en un toque) */}
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            {PRESETS.map((v) => {
              const activo = estado !== 'nc' && actual === v;
              const bajoPiso = v < piso; // regla 1b: bloqueado
              const porCalidad = !permiteAvance(bloqueo, v);
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => onSetPct(v)}
                  disabled={bajoPiso || porCalidad || soloLectura}
                  title={
                    bajoPiso
                      ? `No se puede bajar del piso ${piso}% (último cierre)`
                      : porCalidad
                        ? 'Requiere la inspección de calidad aprobada'
                        : undefined
                  }
                  className={cn(
                    'rounded-md border px-2.5 py-1 text-xs font-semibold tabular-nums transition-colors',
                    bajoPiso || porCalidad
                      ? 'cursor-not-allowed border-border bg-zinc-50 text-zinc-300'
                      : activo
                        ? v >= 100
                          ? 'border-adelante-primary bg-adelante-primary text-adelante-foreground'
                          : 'border-blue-500 bg-blue-500 text-white'
                        : 'border-border bg-white text-foreground hover:bg-zinc-50',
                  )}
                >
                  {v}%
                </button>
              );
            })}
            <input
              type="number"
              min={piso}
              max={100}
              inputMode="numeric"
              value={exacto}
              onChange={(e) => setExacto(e.target.value)}
              onBlur={aplicarExacto}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
              }}
              disabled={soloLectura || bloqueo.inicio}
              aria-label={`% exacto de ${sp.nombre}`}
              className="h-7 w-14 rounded-md border border-border px-1.5 text-center text-xs tabular-nums focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
            />
            <button
              type="button"
              onClick={onNC}
              disabled={actual >= 100 || soloLectura}
              title={
                soloLectura
                  ? 'Solo lectura'
                  : actual >= 100
                    ? 'Una sub-partida al 100% no puede marcarse NC (NC es cuando no se completa)'
                    : 'Marcar no cumplido'
              }
              className={cn(
                'ml-auto flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                actual >= 100
                  ? 'cursor-not-allowed border-border text-muted-foreground opacity-50'
                  : estado === 'nc'
                    ? 'border-red-400 bg-red-50 text-red-700'
                    : 'border-red-200 text-red-700 hover:bg-red-50',
              )}
            >
              <XCircle className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
              NC
            </button>
          </div>

          {control && (control.inspeccion || control.evaluacion) && (
            <ControlCalidadFila
              control={control}
              bloqueo={bloqueo}
              disabled={abriendoControl}
              onAbrir={onAbrirControl}
            />
          )}
        </div>
        {/* Franja derecha: relación con el sprint (negra=actual, roja=atrasada). */}
        <div className={cn('w-1.5 shrink-0', rightClass)} aria-hidden="true" />
      </div>
    </Card>
  );
}

// ============================================================================
// Vista "obra no habilitada"
// ============================================================================

function NoHabilitadaVista({ codigo }: { codigo: string }) {
  return (
    <div className="space-y-4">
      <Volver />
      <Card className="flex flex-col items-center gap-3 p-6 text-center">
        <WarningCircle className="h-9 w-9 text-amber-500" weight="fill" aria-hidden="true" />
        <div>
          <p className="font-mono text-lg font-semibold">{codigo}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Esta obra todavía no está habilitada para captura. Un administrador debe asignarle el
            tipo de casa y el sprint inicial desde <span className="font-medium">Obras</span> en el
            panel de administración.
          </p>
        </div>
      </Card>
    </div>
  );
}

function Volver() {
  return (
    <Link
      to="/campo/avance"
      className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
    >
      <CaretLeft className="h-4 w-4" weight="bold" aria-hidden="true" />
      Obras
    </Link>
  );
}
